<template>
  <div>
    <v-row>
      <v-col v-if="!isOrganizationManager" cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="organization"
          :items="organizations"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('user.registration.organization')"
          class="rounded-lg"
          clearable
          :search-input.sync="orgSearch"
          :filter="localizedFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.name) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.name) }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onOrgIntersect" v-if="hasMoreOrgs" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="training"
          :items="trainings"
          item-value="_id"
          outlined
          dense
          background-color="white"
          :label="$t('user.registration.training')"
          class="rounded-lg"
          clearable
          :search-input.sync="trainingSearch"
          :filter="localizedFilter"
        >
          <template slot="selection" slot-scope="data">
            {{ getLocalisedValue(data.item.title) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ getLocalisedValue(item.title) }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onTrainingIntersect" v-if="hasMoreTrainings" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <TrainersDropDown v-model="trainer" :disabled="loading" />
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-autocomplete
          :disabled="loading"
          hide-details
          v-model="employee"
          :items="employees"
          item-value="_id"
          item-text="name"
          outlined
          dense
          background-color="white"
          :label="$t('reports.employee')"
          class="rounded-lg"
          clearable
          :search-input.sync="employeeSearch"
        >
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ item.name }}
              {{ item.civilIdOrPassport ? `(${item.civilIdOrPassport})` : "" }}
            </div>
          </template>
          <template v-slot:append-item>
            <div v-intersect="onEmployeeIntersect" v-if="hasMoreEmployees" class="text-center pa-2">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-select
          :disabled="loading"
          v-model="status"
          hide-details
          :items="statusArr"
          item-value="value"
          outlined
          dense
          background-color="white"
          :label="$t('reports.revenues.status')"
          class="rounded-lg"
          clearable
        >
          <template v-slot:selection="{ item }">
            {{ formatStatus(item) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex justify-start">
              {{ formatStatus(item) }}
            </div>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-text-field
          v-model="department"
          hide-details
          color="textLight"
          label="Department"
          outlined
          dense
          background-color="white"
          clearable
          class="shrink rounded-lg"
        />
      </v-col>

      <v-col cols="12" sm="6" md="3" lg>
        <v-text-field
          v-model="search"
          hide-details
          prepend-inner-icon="mdi-magnify"
          color="textLight"
          :placeholder="$t('course.search')"
          outlined
          dense
          background-color="white"
          clearable
          class="shrink rounded-lg"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TrainersDropDown from "@/app/user-module/components/TrainersDropDown";

export default {
  name: "RegistrationsFilters",
  components: { TrainersDropDown },
  data() {
    return {
      organization: null,
      training: null,
      trainer: null,
      employee: null,
      employeeList: [],
      organizations: [],
      trainings: [],
      search: null,
      department: null,
      statusArr: ["REGISTERED", "THIRDPARTY", "COMPLETED"],
      status: null,

      orgSearch: null,
      debouncedOrgSearch: null,
      orgSkip: 0,
      orgTotal: 0,
      
      trainingSearch: null,
      debouncedTrainingSearch: null,
      trainingSkip: 0,
      trainingTotal: 0,
      
      employeeSearch: null,
      debouncedEmployeeSearch: null,
      employeeSkip: 0,
      employeeTotal: 0,
      
      limit: 50,
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isOrganizationManager() {
      return (
        this.$store.state.authenticatedUser?.isOrganizationManager ?? false
      );
    },
    hasMoreOrgs() {
      return this.organizations.length < this.orgTotal;
    },
    hasMoreTrainings() {
      return this.trainings.length < this.trainingTotal;
    },
    hasMoreEmployees() {
      return this.employeeList.length < this.employeeTotal;
    },
    employees() {
      return this.employeeList.reduce((result, employee) => {
        const obj = {
          _id: employee._id,
          name: `${employee?.user?.firstName || ""} ${
            employee?.user?.lastName || ""
          }`.trim(),
          email: employee?.user?.email,
          civilIdOrPassport: employee?.user?.civilIdOrPassport || "",
        };

        result.push(obj);

        return result;
      }, []);
    },
  },
  watch: {
    organization() {
      this.$emit("filter", "organization", this.organization);
      if (this.organization) {
        this.employee = null;
        this.employeeSkip = 0;
        this.employeeList = [];
      }
    },
    training() {
      this.$emit("filter", "training", this.training);
    },
    trainer() {
      this.$emit("filter", "trainer", this.trainer);
    },
    employee() {
      this.$emit("filter", "employee", this.employee);
    },
    search() {
      this.$emit("filter", "search", this.search);
    },
    department() {
      if (this.departmentTimeout) clearTimeout(this.departmentTimeout);
      this.departmentTimeout = setTimeout(() => {
        this.$emit("filter", "department", this.department);
      }, 500);
    },
    status() {
      this.$emit("filter", "status", this.status);
    },
    "$route.params.status": {
      handler() {
        if (this.$route.params.status) {
          this.status = this.$route.params.status;
        }
      },
      immediate: true,
    },
    orgSearch(val) {
      if (this.orgTimeout) clearTimeout(this.orgTimeout);
      this.orgTimeout = setTimeout(() => {
        if (this.debouncedOrgSearch !== val) {
          this.orgSkip = 0;
          this.debouncedOrgSearch = val;
        }
      }, 500);
    },
    trainingSearch(val) {
      if (this.trainingTimeout) clearTimeout(this.trainingTimeout);
      this.trainingTimeout = setTimeout(() => {
        if (this.debouncedTrainingSearch !== val) {
          this.trainingSkip = 0;
          this.debouncedTrainingSearch = val;
        }
      }, 500);
    },
    employeeSearch(val) {
      if (this.employeeTimeout) clearTimeout(this.employeeTimeout);
      this.employeeTimeout = setTimeout(() => {
        if (this.debouncedEmployeeSearch !== val) {
          this.employeeSkip = 0;
          this.debouncedEmployeeSearch = val;
        }
      }, 500);
    }
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../course-module/api/query/getTrainings.gql"),
        variables() {
          return {
            limit: this.limit,
            skip: this.trainingSkip,
            filterInput: { search: this.debouncedTrainingSearch || null },
          };
        },
        result({ data }) {
          if (data?.getTrainings) {
            if (this.trainingSkip === 0) {
              this.trainings = data.getTrainings.trainings;
            } else {
              const existingIds = new Set(this.trainings.map(e => e._id));
              const newItems = data.getTrainings.trainings.filter(e => !existingIds.has(e._id));
              this.trainings = [...this.trainings, ...newItems];
            }
            this.trainingTotal = data.getTrainings.totalCount;
          }
        },
      };
    },
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables() {
          return { 
            limit: this.limit, 
            skip: this.orgSkip, 
            filterInput: { search: this.debouncedOrgSearch || null } 
          };
        },
        result({ data }) {
          if (data?.getOrganizations) {
            if (this.orgSkip === 0) {
              this.organizations = data.getOrganizations.organizations;
            } else {
              const existingIds = new Set(this.organizations.map(e => e._id));
              const newItems = data.getOrganizations.organizations.filter(e => !existingIds.has(e._id));
              this.organizations = [...this.organizations, ...newItems];
            }
            this.orgTotal = data.getOrganizations.totalCount;
          }
        },
      };
    },
    getEmployees() {
      return {
        query: require("../../user-module/api/query/getEmployees.gql"),
        variables() {
          return {
            limit: this.limit,
            skip: this.employeeSkip,
            filterInput: { 
              search: this.debouncedEmployeeSearch || null,
              organization: this.organization || null,
            },
          };
        },
        result({ data }) {
          if (data?.getEmployees) {
            if (this.employeeSkip === 0) {
              this.employeeList = data.getEmployees.employees;
            } else {
              const existingIds = new Set(this.employeeList.map(e => e._id));
              const newItems = data.getEmployees.employees.filter(e => !existingIds.has(e._id));
              this.employeeList = [...this.employeeList, ...newItems];
            }
            this.employeeTotal = data.getEmployees.totalCount;
          }
        },
      };
    },
  },
  methods: {
    onOrgIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreOrgs && !this.$apollo.queries.getOrganizations.loading) {
        this.orgSkip += this.limit;
        this.$apollo.queries.getOrganizations.fetchMore({
          variables: {
            skip: this.orgSkip,
            limit: this.limit,
            filterInput: { search: this.orgSearch || null },
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getOrganizations: {
                __typename: previousResult.getOrganizations.__typename,
                totalCount: fetchMoreResult.getOrganizations.totalCount,
                organizations: [
                  ...previousResult.getOrganizations.organizations,
                  ...fetchMoreResult.getOrganizations.organizations,
                ],
              },
            };
          },
        });
      }
    },
    onTrainingIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreTrainings && !this.$apollo.queries.getTrainings.loading) {
        this.trainingSkip += this.limit;
        this.$apollo.queries.getTrainings.fetchMore({
          variables: {
            skip: this.trainingSkip,
            limit: this.limit,
            filterInput: { search: this.trainingSearch || null },
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getTrainings: {
                __typename: previousResult.getTrainings.__typename,
                totalCount: fetchMoreResult.getTrainings.totalCount,
                trainings: [
                  ...previousResult.getTrainings.trainings,
                  ...fetchMoreResult.getTrainings.trainings,
                ],
              },
            };
          },
        });
      }
    },
    onEmployeeIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && this.hasMoreEmployees && !this.$apollo.queries.getEmployees.loading) {
        this.employeeSkip += this.limit;
        this.$apollo.queries.getEmployees.fetchMore({
          variables: {
            skip: this.employeeSkip,
            limit: this.limit,
            filterInput: { 
              search: this.employeeSearch || null,
              organization: this.organization || null,
            },
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              getEmployees: {
                __typename: previousResult.getEmployees.__typename,
                totalCount: fetchMoreResult.getEmployees.totalCount,
                employees: [
                  ...previousResult.getEmployees.employees,
                  ...fetchMoreResult.getEmployees.employees,
                ],
              },
            };
          },
        });
      }
    },
    localizedFilter(item, queryText, itemText) {
      const text = this.getLocalisedValue(item.name || item.title) || "";
      return text.toLowerCase().includes(queryText.toLowerCase());
    },
    async refetchEmployees() {
      await this.$apollo.queries.getEmployees.refetch({
        skip: 0,
        limit: this.limit,
        filterInput: {
          organization: this.organization || null,
          search: this.employeeSearch || null,
        },
      });
    },
    formatStatus(status) {
      if (!status) return "-";
      if (status === "THIRDPARTY") return "ThirdParty";
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
  },
};
</script>
